import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useUpdateContract } from "../../api/contracts";
import { useToast } from "../../components/ToastProvider";
import { Contract } from "../../types/dtos";

export function EditContractModal({ contract, onClose }: { contract: Contract | null; onClose: () => void }) {
  const [billingDay, setBillingDay] = useState("10");
  const [monthlyValue, setMonthlyValue] = useState("");
  const [readjustmentIndex, setReadjustmentIndex] = useState<"IPCA" | "IGPM" | "NONE">("NONE");
  const [error, setError] = useState<string | null>(null);

  const update = useUpdateContract();
  const toast = useToast();

  useEffect(() => {
    if (contract) {
      setBillingDay(String(contract.billingDay));
      setMonthlyValue(String(contract.monthlyValue));
      setReadjustmentIndex(contract.readjustmentIndex);
      setError(null);
    }
  }, [contract]);

  if (!contract) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!contract) return;
    const day = Number(billingDay);
    const value = Number(monthlyValue.replace(",", "."));
    if (!Number.isInteger(day) || day < 1 || day > 28) {
      setError("Informe um dia de vencimento entre 1 e 28.");
      return;
    }
    if (!Number.isFinite(value) || value < 0) {
      setError("Informe um valor de mensalidade válido.");
      return;
    }
    try {
      await update.mutateAsync({ id: contract.id, input: { billingDay: day, monthlyValue: value, readjustmentIndex } });
      toast(`Contrato ${contract.code} atualizado.`);
      onClose();
    } catch {
      setError("Não foi possível salvar as alterações.");
    }
  }

  return (
    <Modal backdrop="static" keyboard={false} show onHide={onClose} centered className="nt-dialog">
      <form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 20, fontFamily: "var(--font-heading)" }}>Editar {contract.code}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column gap-3">
          <div style={{ fontSize: 13 }} className="text-secondary">
            {contract.customer.razaoSocial} — {contract.plan.application.name} / {contract.plan.name}
          </div>

          <div>
            <label className="form-label" style={{ fontSize: 12, fontWeight: 700 }}>
              Dia de vencimento
            </label>
            <input
              type="number"
              min={1}
              max={28}
              className="form-control"
              value={billingDay}
              onChange={(e) => setBillingDay(e.target.value)}
            />
            <div className="text-secondary mt-1" style={{ fontSize: 11.5 }}>
              Os títulos futuros gerados para este contrato passam a vencer nesse dia do mês — os já emitidos não mudam.
            </div>
          </div>

          <div>
            <label className="form-label" style={{ fontSize: 12, fontWeight: 700 }}>
              Mensalidade (R$)
            </label>
            <input className="form-control" value={monthlyValue} onChange={(e) => setMonthlyValue(e.target.value)} />
          </div>

          <div>
            <label className="form-label" style={{ fontSize: 12, fontWeight: 700 }}>
              Índice de reajuste
            </label>
            <select
              className="form-select"
              value={readjustmentIndex}
              onChange={(e) => setReadjustmentIndex(e.target.value as "IPCA" | "IGPM" | "NONE")}
            >
              <option value="NONE">Nenhum</option>
              <option value="IPCA">IPCA</option>
              <option value="IGPM">IGP-M</option>
            </select>
          </div>

          {error && (
            <div className="text-danger" style={{ fontSize: 12.5 }}>
              {error}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-danger" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-success" disabled={update.isPending}>
            Salvar
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
